/**
 * Set of the day, shared between the dashboard card, the X&O shelf, the
 * sheet and the admin page.
 *
 * A set is a drills row in the X&O section with a sotd_date: the day it is
 * the set. Everything about it (clip, title, tags, the one coaching point)
 * is a drill already, so saving, notes, search and the community embed all
 * work without a second content type. What this file adds is the feed, who
 * sees it, the streak, and the one-tap vote.
 */
import { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { mapRow } from "../hooks/useDrills";
import { loadUserData, saveUserData } from "../utils/userData";

// Sets live in their own section so they never show up in X&O or the
// admin library as ordinary drills. The page that shows them is the Playbook.
export const SOTD_SECTION = "sets";
export const SOTD_ROUTE = "playbook";
const COLS = "id, title, category, type, coach_id, level, duration, mux_playback_id, mux_asset_id, description, ai_description, tags, is_new, publish_at, coaching_points, has_narration, ai_enriched_at, xo_category, xo_subcategory, categories, format, section, sort_order, created_at, status, sotd_date, sotd_kind";

export const todayKey = (d = new Date()) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export function kindWord(row, cap = false) {
  const k = row?.sotdKind === "action" ? "action" : "set";
  return cap ? k[0].toUpperCase() + k.slice(1) : k;
}

export function fmtSotdDate(key) {
  if (!key) return "";
  const t = todayKey();
  if (key === t) return "Today";
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (key === todayKey(y)) return "Yesterday";
  const d = new Date(`${key}T12:00:00`);
  return Number.isNaN(d.getTime()) ? key : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function mapSotd(row) {
  const m = mapRow(row);
  return {
    ...m,
    section: SOTD_SECTION,
    sotdDate: row.sotd_date || null,
    sotdKind: row.sotd_kind || "set",
    point: (Array.isArray(row.coaching_points) ? row.coaching_points[0] : row.coaching_points) || "",
    // The raw row, for the drill embed and the save key.
    mux_playback_id: row.mux_playback_id || null,
  };
}

// ── The feed: every set up to and including today, newest first ─────────
let cache = null, cacheAt = 0, inflight = null;
const TTL = 3 * 60 * 1000;

export async function loadSotdFeed({ force = false } = {}) {
  if (!force && cache && Date.now() - cacheAt < TTL) return cache;
  if (inflight && !force) return inflight;
  inflight = (async () => {
    try {
      const { data, error } = await supabase
        .from("drills").select(COLS)
        .not("sotd_date", "is", null)
        .lte("sotd_date", todayKey())
        .or("status.eq.published,status.is.null")
        .is("deleted_at", null)
        .order("sotd_date", { ascending: false })
        .limit(120);
      if (error) throw error;
      cache = (data || []).map(mapSotd);
      cacheAt = Date.now();
      return cache;
    } catch {
      // A column that doesn't exist yet (migration not run) lands here too.
      return cache || [];
    } finally { inflight = null; }
  })();
  return inflight;
}

export function invalidateSotd() { cache = null; cacheAt = 0; }

export function useSotdFeed(enabled = true) {
  const [rows, setRows] = useState(() => cache || []);
  const [loading, setLoading] = useState(!cache);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    loadSotdFeed().then(r => { if (alive) { setRows(r); setLoading(false); } });
    return () => { alive = false; };
  }, [enabled]);
  return { rows, loading, today: rows[0] && rows[0].sotdDate === todayKey() ? rows[0] : null, latest: rows[0] || null };
}

// ── Who sees it ────────────────────────────────────────────────────────
// Team coaches by default. A player development coach doesn't, unless they
// switch it on from the X&O page. The switch is one key in user_data so it
// follows the account, not the device.
const PREF_KEY = "sotd_pref";
let prefCache = null;

export async function loadSotdPref() {
  if (prefCache) return prefCache;
  try { prefCache = await loadUserData(PREF_KEY, {}); } catch { prefCache = {}; }
  return prefCache || {};
}
export async function saveSotdPref(next) {
  prefCache = { ...(prefCache || {}), ...next };
  try { await saveUserData(PREF_KEY, prefCache); } catch {}
  return prefCache;
}

export function sotdVisible(coachType, pref) {
  if (pref && typeof pref.on === "boolean") return pref.on;
  return coachType !== "pd";
}

let coachTypeCache;
export async function loadCoachType() {
  if (coachTypeCache !== undefined) return coachTypeCache;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return (coachTypeCache = null);
    const { data } = await supabase.from("profiles").select("coach_type").eq("id", user.id).maybeSingle();
    coachTypeCache = data?.coach_type || null;
  } catch { coachTypeCache = null; }
  return coachTypeCache;
}

// Both at once, for the card and the shelf.
export function useSotdVisibility() {
  const [state, setState] = useState({ ready: false, coachType: null, pref: {} });
  useEffect(() => {
    let alive = true;
    Promise.all([loadCoachType(), loadSotdPref()]).then(([coachType, pref]) => {
      if (alive) setState({ ready: true, coachType, pref: pref || {} });
    });
    return () => { alive = false; };
  }, []);
  const setOn = async (on) => {
    const pref = await saveSotdPref({ on });
    setState(s => ({ ...s, pref }));
  };
  return { ...state, visible: state.ready && sotdVisible(state.coachType, state.pref), setOn };
}

// ── The streak ─────────────────────────────────────────────────────────
// Days the coach opened a set. Not days they were sent one: a streak you
// can lose while asleep is a nag, not a habit.
const STREAK_KEY = "sotd_streak";
let streakCache = null;

export async function loadStreakDays() {
  if (streakCache) return streakCache;
  try { const d = await loadUserData(STREAK_KEY, { days: [] }); streakCache = Array.isArray(d?.days) ? d.days : []; }
  catch { streakCache = []; }
  return streakCache;
}

export async function recordSotdOpen() {
  const days = await loadStreakDays();
  const t = todayKey();
  if (days.includes(t)) return days;
  streakCache = [...days, t].slice(-180);
  try { await saveUserData(STREAK_KEY, { days: streakCache }); } catch {}
  return streakCache;
}

// Consecutive days ending today or yesterday. Yesterday counts so the
// number doesn't drop to zero before the coach has had their coffee.
export function streakFrom(days) {
  const set = new Set(days || []);
  const d = new Date();
  if (!set.has(todayKey(d))) d.setDate(d.getDate() - 1);
  let n = 0;
  while (set.has(todayKey(d))) { n += 1; d.setDate(d.getDate() - 1); }
  return n;
}

export function useStreak() {
  const [days, setDays] = useState(() => streakCache || []);
  useEffect(() => { let alive = true; loadStreakDays().then(d => { if (alive) setDays(d); }); return () => { alive = false; }; }, []);
  const bump = async () => { const d = await recordSotdOpen(); setDays(d); };
  return { streak: streakFrom(days), openedToday: days.includes(todayKey()), bump };
}

// ── "Would you run this?" ───────────────────────────────────────────────
export const VOTES = [
  { id: "yes",   label: "Yes" },
  { id: "maybe", label: "Maybe" },
  { id: "no",    label: "No" },
];

export async function loadMyVotes(ids) {
  if (!ids?.length) return {};
  try {
    const { data } = await supabase.from("sotd_votes").select("drill_id, vote").in("drill_id", ids);
    const out = {};
    (data || []).forEach(r => { out[r.drill_id] = r.vote; });
    return out;
  } catch { return {}; }
}

export async function loadVoteCounts(ids) {
  if (!ids?.length) return {};
  try {
    const { data } = await supabase.rpc("sotd_vote_counts", { p_ids: ids });
    const out = {};
    (data || []).forEach(r => { out[r.drill_id] = { yes: r.yes || 0, maybe: r.maybe || 0, no: r.no || 0 }; });
    return out;
  } catch { return {}; }
}

export async function castVote(drillId, vote) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  if (!vote) {
    const { error } = await supabase.from("sotd_votes").delete().eq("user_id", user.id).eq("drill_id", drillId);
    return !error;
  }
  const { error } = await supabase.from("sotd_votes").upsert({ user_id: user.id, drill_id: drillId, vote }, { onConflict: "user_id,drill_id" });
  return !error;
}

export function pctWouldRun(counts) {
  if (!counts) return null;
  const total = (counts.yes || 0) + (counts.maybe || 0) + (counts.no || 0);
  if (!total) return null;
  return { pct: Math.round((counts.yes / total) * 100), total };
}

// The drill embed for a community post, with the section set so the card
// opens the X&O page rather than Drills & Games.
export function sotdEmbed(row) {
  return {
    type: "drill",
    drill_id: String(row.id),
    title: row.title || "Set of the day",
    section: SOTD_ROUTE,
    category: kindWord(row, true),
    duration: row.duration || null,
    thumbnail_url: row.muxPlaybackId ? `https://image.mux.com/${row.muxPlaybackId}/thumbnail.jpg?width=640&height=400&fit_mode=smartcrop` : null,
  };
}
